import { useEffect, useState } from "react";
import { Calculator, Check, ChevronDown, ChevronUp, Loader2, Radar } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../../lib/api";

const MP_BADGE = {
  ebay: "bg-sky-400/15 text-sky-300",
  amazon: "bg-orange-400/15 text-orange-300",
  tiktok: "bg-rose-400/15 text-rose-300",
};

export default function PricingPage() {
  const [settings, setSettings] = useState(null);
  const [channelFees, setChannelFees] = useState({});
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [competitors, setCompetitors] = useState({});
  const [edits, setEdits] = useState({});
  const [scanning, setScanning] = useState(false);
  const [busyRow, setBusyRow] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: o }] = await Promise.all([
      api.get("/admin/pricing/settings"),
      api.get("/admin/pricing/overview"),
    ]);
    setSettings(s.settings);
    setChannelFees(s.channel_fees);
    setRows(o.products);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/pricing/settings", {
        target_margin: Number(settings.target_margin),
        min_margin: Number(settings.min_margin),
        undercut_percent: Number(settings.undercut_percent),
        auto_apply: !!settings.auto_apply,
      });
      setSettings(data.settings);
      toast.success("Pricing settings saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/pricing/scan");
      const applied = data.report.filter((r) => r.applied).length;
      toast.success(`Competitor scan complete (MOCKED) — ${applied} price${applied === 1 ? "" : "s"} auto-adjusted`);
      setCompetitors({});
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setScanning(false);
  };

  const toggleExpand = async (row) => {
    const next = expanded === row.id ? null : row.id;
    setExpanded(next);
    if (next) {
      setEdits((e) => ({
        ...e,
        [row.id]: {
          shipping_cost: row.shipping_cost,
          target_margin: row.target_margin,
          min_margin: row.min_margin,
          auto_pricing: row.auto_pricing,
        },
      }));
      if (!competitors[row.id]) {
        const { data } = await api.get(`/admin/pricing/product/${row.id}/competitors`);
        setCompetitors((c) => ({ ...c, [row.id]: data.competitors }));
      }
    }
  };

  const saveProduct = async (id) => {
    setBusyRow(id);
    try {
      const e = edits[id];
      await api.patch(`/admin/pricing/product/${id}`, {
        shipping_cost: Number(e.shipping_cost),
        target_margin: e.target_margin === "" ? null : Number(e.target_margin),
        min_margin: e.min_margin === "" ? null : Number(e.min_margin),
        auto_pricing: !!e.auto_pricing,
      });
      toast.success("Product pricing updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusyRow(null);
  };

  const apply = async (id, price, label) => {
    setBusyRow(id);
    try {
      await api.post(`/admin/pricing/product/${id}/apply`, { price, reason: label });
      toast.success(`Price set to ${fmt(price)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusyRow(null);
  };

  if (!settings) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />;

  return (
    <div data-testid="admin-pricing-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Pricing engine</p>
          <h1 className="font-heading text-2xl font-bold text-white">Profit calculator & competitive pricing</h1>
          <p className="mt-1 text-xs text-slate-500">
            Prices are solved from supplier cost + shipping + channel fees at your target margin. Competitor monitoring is <span className="text-amber-500">MOCKED</span> — real eBay/Amazon/TikTok APIs plug in later. Prices never drop below your minimum margin.
          </p>
        </div>
        <button data-testid="run-competitor-scan-btn" onClick={scan} disabled={scanning} className="btn-gold !px-5 !py-2.5 text-sm disabled:opacity-60">
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />}
          {scanning ? "Scanning competitors…" : "Run competitor scan"}
        </button>
      </div>

      <div className="card-surface mb-6 p-5" data-testid="pricing-settings-card">
        <p className="mb-4 flex items-center gap-2 font-heading text-sm font-semibold text-white"><Calculator size={15} className="text-amber-400" /> Global pricing rules</p>
        <div className="grid gap-4 sm:grid-cols-4">
          <label className="text-xs text-slate-400">
            Target profit margin %
            <input data-testid="setting-target-margin" type="number" value={settings.target_margin}
              onChange={(e) => setSettings((s) => ({ ...s, target_margin: e.target.value }))} className="input-dark mt-1" />
          </label>
          <label className="text-xs text-slate-400">
            Minimum margin % (floor)
            <input data-testid="setting-min-margin" type="number" value={settings.min_margin}
              onChange={(e) => setSettings((s) => ({ ...s, min_margin: e.target.value }))} className="input-dark mt-1" />
          </label>
          <label className="text-xs text-slate-400">
            Undercut competitors by %
            <input data-testid="setting-undercut" type="number" value={settings.undercut_percent}
              onChange={(e) => setSettings((s) => ({ ...s, undercut_percent: e.target.value }))} className="input-dark mt-1" />
          </label>
          <label className="flex items-end gap-2 pb-2 text-xs text-slate-300">
            <input data-testid="setting-auto-apply" type="checkbox" checked={!!settings.auto_apply}
              onChange={(e) => setSettings((s) => ({ ...s, auto_apply: e.target.checked }))} className="h-4 w-4 accent-amber-400" />
            Auto-apply scan prices store-wide
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <button data-testid="save-pricing-settings-btn" onClick={saveSettings} className="btn-gold !px-5 !py-2 text-xs">Save rules</button>
          <p className="text-[11px] text-slate-500">
            Fees — {Object.values(channelFees).map((f) => `${f.label}: ${(f.pct * 100).toFixed(2)}%${f.fixed ? ` + ${fmt(f.fixed)}` : ""}`).join(" · ")}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((r) => {
          const shop = r.channels.find((c) => c.channel === "shop");
          const open = expanded === r.id;
          return (
            <div key={r.id} className="card-surface overflow-hidden" data-testid={`pricing-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-4 p-4">
                <img src={r.image} alt="" className="h-12 w-12 rounded-xl object-cover" />
                <div className="min-w-40 flex-1">
                  <p className="truncate font-medium text-white">{r.name}</p>
                  <p className="text-[11px] text-slate-500">Landed cost {fmt(r.landed_cost)} ({fmt(r.cost)} + {fmt(r.shipping_cost)} ship)</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Price</p>
                  <p className="font-heading font-bold text-white" data-testid={`pricing-current-${r.id}`}>{fmt(r.price)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Profit / sale</p>
                  <p className={`font-semibold ${shop.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid={`pricing-profit-${r.id}`}>
                    {fmt(shop.profit)} <span className="text-xs">({shop.margin}%)</span>
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Suggested ({r.target_margin}%)</p>
                  <p className="font-mono text-sm text-amber-300" data-testid={`pricing-suggested-${r.id}`}>{r.suggested_price ? fmt(r.suggested_price) : "—"}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Floor ({r.min_margin}%)</p>
                  <p className="font-mono text-sm text-slate-400" data-testid={`pricing-floor-${r.id}`}>{r.floor_price ? fmt(r.floor_price) : "—"}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Competitor low</p>
                  <p className="font-mono text-sm text-sky-300" data-testid={`pricing-competitor-${r.id}`}>
                    {r.competitors ? fmt(r.competitors.lowest_landed) : "not scanned"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {r.suggested_price && Math.abs(r.suggested_price - r.price) >= 0.5 && (
                    <button
                      data-testid={`pricing-apply-${r.id}`}
                      onClick={() => apply(r.id, r.suggested_price, `manual apply of ${r.target_margin}% target margin`)}
                      disabled={busyRow === r.id}
                      className="rounded-full border border-amber-400/40 px-3 py-1.5 text-[11px] font-semibold text-amber-300 hover:bg-amber-400/10 disabled:opacity-50"
                    >
                      <Check size={11} className="mr-1 inline" />
                      {r.suggested_price > r.price ? "Raise to" : "Lower to"} {fmt(r.suggested_price)}
                    </button>
                  )}
                  <button data-testid={`pricing-expand-${r.id}`} onClick={() => toggleExpand(r)} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-white">
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {open && (
                <div className="grid gap-6 border-t border-white/[0.06] bg-white/[0.02] p-5 lg:grid-cols-2" data-testid={`pricing-detail-${r.id}`}>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Profit breakdown by channel (at current price)</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="py-1.5">Channel</th><th>Price</th><th>Fees</th><th>Landed cost</th><th>Profit</th><th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.channels.map((c) => (
                          <tr key={c.channel} className="border-t border-white/[0.05]" data-testid={`breakdown-${c.channel}-${r.id}`}>
                            <td className="py-1.5 text-slate-300">{c.label}</td>
                            <td className="text-white">{fmt(c.price)}</td>
                            <td className="text-slate-400">−{fmt(c.fee)}</td>
                            <td className="text-slate-400">−{fmt(c.cost + c.shipping)}</td>
                            <td className={c.profit >= 0 ? "text-emerald-400" : "text-rose-400"}>{fmt(c.profit)}</td>
                            <td className={c.margin >= r.min_margin ? "text-emerald-400" : "text-rose-400"}>{c.margin}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <label className="text-[11px] text-slate-400">Shipping cost $
                        <input data-testid={`edit-shipping-${r.id}`} type="number" step="0.01" className="input-dark mt-1 !px-2 !py-1.5 text-xs"
                          value={edits[r.id]?.shipping_cost ?? ""}
                          onChange={(e) => setEdits((s) => ({ ...s, [r.id]: { ...s[r.id], shipping_cost: e.target.value } }))} />
                      </label>
                      <label className="text-[11px] text-slate-400">Target margin %
                        <input data-testid={`edit-target-${r.id}`} type="number" className="input-dark mt-1 !px-2 !py-1.5 text-xs"
                          value={edits[r.id]?.target_margin ?? ""}
                          onChange={(e) => setEdits((s) => ({ ...s, [r.id]: { ...s[r.id], target_margin: e.target.value } }))} />
                      </label>
                      <label className="text-[11px] text-slate-400">Min margin %
                        <input data-testid={`edit-min-${r.id}`} type="number" className="input-dark mt-1 !px-2 !py-1.5 text-xs"
                          value={edits[r.id]?.min_margin ?? ""}
                          onChange={(e) => setEdits((s) => ({ ...s, [r.id]: { ...s[r.id], min_margin: e.target.value } }))} />
                      </label>
                      <label className="flex items-end gap-2 pb-1.5 text-[11px] text-slate-300">
                        <input data-testid={`edit-auto-${r.id}`} type="checkbox" className="h-4 w-4 accent-amber-400"
                          checked={!!edits[r.id]?.auto_pricing}
                          onChange={(e) => setEdits((s) => ({ ...s, [r.id]: { ...s[r.id], auto_pricing: e.target.checked } }))} />
                        Auto-price this product
                      </label>
                    </div>
                    <button data-testid={`edit-save-${r.id}`} onClick={() => saveProduct(r.id)} disabled={busyRow === r.id}
                      className="btn-gold mt-3 !px-4 !py-1.5 text-xs disabled:opacity-50">
                      Save product rules
                    </button>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Competitor listings <span className="text-amber-500">(MOCKED)</span>
                    </p>
                    {competitors[r.id]?.length ? (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500"><th className="py-1.5">Marketplace</th><th>Seller</th><th>Price</th><th>Shipping</th><th>Landed</th></tr>
                        </thead>
                        <tbody>
                          {competitors[r.id].map((c) => (
                            <tr key={c.id} className="border-t border-white/[0.05]" data-testid={`competitor-${c.id}`}>
                              <td className="py-1.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${MP_BADGE[c.marketplace]}`}>{c.marketplace}</span></td>
                              <td className="text-slate-400">{c.seller}</td>
                              <td className="text-white">{fmt(c.price)}</td>
                              <td className="text-slate-400">{c.shipping ? fmt(c.shipping) : "Free"}</td>
                              <td className="text-sky-300">{fmt(c.landed)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-slate-500">No competitor data yet — run a scan.</p>
                    )}
                    {r.price_history?.length > 0 && (
                      <div className="mt-4">
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Price history</p>
                        {r.price_history.map((h, i) => (
                          <p key={i} className="text-[11px] text-slate-500">
                            {fmt(h.from)} → <span className="text-amber-300">{fmt(h.to)}</span> · {h.reason} · {h.at.slice(0, 10)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
